import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CarrierWebhookEvent, CatalogService } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-carrier-webhook-events',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, TableModule],
  template: `
    <ain-page [title]="t('catalog.integrations.webhookAuditTitle')" [description]="t('catalog.integrations.webhookAuditDescription')">
      <ain-inventory-page-hint pageKey="carrierWebhookEvents" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>
      @if (loading()) { <ain-state type="loading" [title]="t('catalog.loading')" /> }
      @else if (error()) {
        <ain-state type="error" [title]="t('catalog.integrations.loadFailed')" [message]="error()" />
      }
      @else {
        <p-table [value]="events()" [paginator]="true" [rows]="20">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.integrations.externalEventId') }}</th>
              <th>{{ t('catalog.integrations.carrier') }}</th>
              <th>{{ t('catalog.integrations.trackingNumber') }}</th>
              <th>{{ t('catalog.integrations.mappedStatus') }}</th>
              <th>{{ t('columns.status') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.externalEventId }}</td>
              <td>{{ row.carrierCode }}</td>
              <td>{{ row.trackingNumber || '-' }}</td>
              <td>{{ row.mappedDeliveryStatus }}</td>
              <td>{{ row.processingStatus }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="5">{{ t('catalog.integrations.noWebhookEvents') }}</td></tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryCarrierWebhookEventsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  readonly loading = signal(true);
  readonly error = signal('');
  readonly events = signal<CarrierWebhookEvent[]>([]);
  t = (k: string) => this.localization.translate(k);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload(false)
    );
    this.reload(true);
  }

  reload(showSpinner = true): void {
    if (showSpinner) this.loading.set(true);
    this.error.set('');
    this.service.listCarrierWebhookEvents().subscribe({
      next: rows => { this.events.set(rows); this.loading.set(false); },
      error: () => {
        this.events.set([]);
        this.error.set(this.t('catalog.integrations.loadFailed'));
        this.loading.set(false);
      }
    });
  }
}
